type LatLng = [number, number];

export type BuildingPolygon = LatLng[];

export type BuildingPolygons = Record<string, BuildingPolygon>;

export type ProximityLine = {
  from: string;
  to: string;
  distance_feet: number;
  p1: LatLng;
  p2: LatLng;
};

export type ProximityResult = {
  groups: string[][];
  lines: ProximityLine[];
};

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS_FT = 20902231;
const FEET_PER_MILE = 5280;

const haversineFeet = (p1: LatLng, p2: LatLng): number => {
  const [lat1, lng1] = p1.map((v) => v * DEG_TO_RAD);
  const [lat2, lng2] = p2.map((v) => v * DEG_TO_RAD);
  const dlat = lat2 - lat1;
  const dlng = lng2 - lng1;
  const a =
    Math.sin(dlat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlng / 2) ** 2;
  return 2 * EARTH_RADIUS_FT * Math.asin(Math.sqrt(a));
};

const closestPointOnSegment = (
  a: LatLng,
  b: LatLng,
  p: LatLng,
): { point: LatLng; t: number } => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { point: [...a] as LatLng, t: 0 };
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return { point: [a[0] + t * dx, a[1] + t * dy], t };
};

const minDistBetweenPolygons = (
  polyA: BuildingPolygon,
  polyB: BuildingPolygon,
): { dist: number; p1: LatLng; p2: LatLng } => {
  let minDist = Infinity;
  let bestP1: LatLng | null = null;
  let bestP2: LatLng | null = null;

  for (let i = 0; i < polyA.length; i++) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % polyA.length];
    for (let j = 0; j < polyB.length; j++) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % polyB.length];

      const candidates: [LatLng, LatLng][] = [
        [a1, closestPointOnSegment(b1, b2, a1).point],
        [a2, closestPointOnSegment(b1, b2, a2).point],
        [closestPointOnSegment(a1, a2, b1).point, b1],
        [closestPointOnSegment(a1, a2, b2).point, b2],
      ];

      for (const [cp1, cp2] of candidates) {
        const d = haversineFeet(cp1, cp2);
        if (d < minDist) {
          minDist = d;
          bestP1 = cp1;
          bestP2 = cp2;
        }
      }
    }
  }

  return { dist: minDist, p1: bestP1!, p2: bestP2! };
};

export const groupBuildingsByProximity = (
  buildings: BuildingPolygons,
  thresholdFeet: number = 100,
): ProximityResult => {
  const ids = Object.keys(buildings);

  const edges: Array<{
    from: string;
    to: string;
    dist: number;
    p1: LatLng;
    p2: LatLng;
  }> = [];

  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const result = minDistBetweenPolygons(
        buildings[ids[i]],
        buildings[ids[j]],
      );
      if (result.dist <= thresholdFeet) {
        edges.push({
          from: ids[i],
          to: ids[j],
          dist: result.dist,
          p1: result.p1,
          p2: result.p2,
        });
      }
    }
  }

  const parent: Record<string, string> = {};
  ids.forEach((id) => (parent[id] = id));

  const find = (x: string): string => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };

  const union = (a: string, b: string) => {
    parent[find(a)] = find(b);
  };

  edges.forEach((e) => union(e.from, e.to));

  const groupMap: Record<string, string[]> = {};
  ids.forEach((id) => {
    const root = find(id);
    if (!groupMap[root]) groupMap[root] = [];
    groupMap[root].push(id);
  });

  const groups = Object.values(groupMap).sort((a, b) => b.length - a.length);

  edges.sort((a, b) => a.dist - b.dist);

  const mstParent: Record<string, string> = {};
  ids.forEach((id) => (mstParent[id] = id));

  const mstFind = (x: string): string => {
    while (mstParent[x] !== x) {
      mstParent[x] = mstParent[mstParent[x]];
      x = mstParent[x];
    }
    return x;
  };

  const mstUnion = (a: string, b: string) => {
    mstParent[mstFind(a)] = mstFind(b);
  };

  const mstEdges: ProximityLine[] = [];
  edges.forEach((e) => {
    if (mstFind(e.from) !== mstFind(e.to)) {
      mstUnion(e.from, e.to);
      mstEdges.push({
        from: e.from,
        to: e.to,
        distance_feet: Math.round(e.dist * 10) / 10,
        p1: e.p1,
        p2: e.p2,
      });
    }
  });

  return { groups, lines: mstEdges };
};

export const groupBuildingsByRadius = (
  buildings: BuildingPolygons,
  radiusMiles: number = 10,
): ProximityResult =>
  groupBuildingsByProximity(buildings, radiusMiles * FEET_PER_MILE);
